/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Image} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import {getPDPath} from '../../../utils/custom-utils'

/**
 * Simple ImageTileWithLInk component that can be used inside any Layout component.
 * @param image Object containing the image url, _type and focalPoint.
 * @returns {JSX.Element}
 */
export const ImageWithLink = ({ITCLink, alt, imageLazyload, bottomSpace, target, custom}) => {
    const {site, locale} = useMultiSite()
    const defaultImageUrl = custom?.image?.disBaseLink
    const mobileImageUrl = custom?.mobileImage?.disBaseLink || defaultImageUrl
    const tabletImageUrl = custom?.tabletImage?.url || mobileImageUrl
    const marginBottom = bottomSpace
    const isLazy = imageLazyload == 'Yes'
    const categoryUrl = getPDPath(ITCLink, site, locale)
    const targetLink = target === 'New-Window' ? '_blank' : '_self'
    const segment = useSegment()

    return (
        <Box
            className="image-with-link"
            marginBottom={{base: '0', sm: marginBottom}}
            display={'inline-block'}
            w="full"
            onClick={(e) => {
                // segment
                segment.sendPanelSelectedEvent(e, {})
            }}
        >
            <Link to={categoryUrl} display="block" target={targetLink}>
                <picture>
                    <source srcSet={`${mobileImageUrl}`} media="(max-width: 47.99em)" />
                    <source
                        srcSet={`${tabletImageUrl}`}
                        media="(min-width: 48em) and (max-width: 63.99em)"
                    />
                    <Image
                        src={defaultImageUrl}
                        alt={alt}
                        loading={isLazy ? 'lazy' : 'eager'}
                        style={{margin: '0 auto'}}
                        fetchpriority={isLazy ? undefined : 'high'}
                    />
                </picture>
            </Link>
        </Box>
    )
}

ImageWithLink.propTypes = {
    tabletImage: PropTypes.object,
    mobileImage: PropTypes.object,
    ITCLink: PropTypes.string,
    alt: PropTypes.string,
    bottomSpace: PropTypes.string,
    imageLazyload: PropTypes.string,
    target: PropTypes.string,
    custom: PropTypes.object
}

export default ImageWithLink
